import { Link, useRouterState } from "@tanstack/react-router";
import { Children, useEffect, useRef, useState } from "react";
import { BookOpen, CalendarDays, Check, ChevronDown, Clock, Lightbulb } from "lucide-react";
import { Layout } from "@/components/Layout";
import { ShareBar } from "@/components/ShareBar";
import { relatedPosts, POSTS, CATEGORIES, seriesPosition, type CategoryId } from "@/lib/montessori-koulu-posts";
import { CATEGORY_HERO } from "@/lib/article-heroes";

export interface BlogPostProps {
  eyebrow: string;
  title: string;
  lead: string;
  readingMinutes: number;
  publishedISO: string; // YYYY-MM-DD
  updatedISO?: string;
  slug?: string;
  /** Optional article-specific hero; falls back to the category image. */
  heroImage?: string;
  heroAlt?: string;
  children: React.ReactNode;
}

/** Age-matched product picks per learning-hub category. */
const CATEGORY_PRODUCTS = {
  perusteet: [
    { to: "/kauppa", label: "Koko mallisto", sub: "0 – 6 v" },
    { to: "/oppimistorni", label: "Oppimistornit", sub: "12 kk – 5 v" },
    { to: "/pikler-kolmio", label: "Pikler-kolmiot", sub: "Ikä vaihtelee mallista" },
  ],
  vauva: [
    { to: "/montessori-sanky", label: "Montessori-sängyt", sub: "0 – 3 v" },
    { to: "/montessori-vauva", label: "Vauvan mallisto", sub: "0 – 12 kk" },
    { to: "/iglu", label: "IGLU-palikat", sub: "6 kk +" },
  ],
  taapero: [
    { to: "/oppimistorni", label: "Oppimistornit", sub: "12 kk – 5 v" },
    { to: "/montessori-lelut", label: "Montessori-lelut", sub: "1 – 3 v" },
    { to: "/iglu", label: "IGLU-palikat", sub: "6 kk +" },
  ],
  itsenaisyys: [
    { to: "/lasten-vaaterekki", label: "Vaaterekit", sub: "18 kk +" },
    { to: "/oppimistorni", label: "Oppimistornit", sub: "12 kk – 5 v" },
    { to: "/lelulaatikko", label: "Lelulaatikot", sub: "1 v +" },
  ],
  koti: [
    { to: "/lastenhuoneen-kalusteet", label: "Lastenhuoneen kalusteet", sub: "0 – 6 v" },
    { to: "/lelulaatikko", label: "Lelulaatikot", sub: "1 v +" },
    { to: "/kauppa", label: "Koko mallisto", sub: "0 – 6 v" },
  ],
  motoriikka: [
    { to: "/pikler-kolmio", label: "Pikler-kolmiot", sub: "Ikä vaihtelee mallista" },
    { to: "/kiipeilyteline", label: "Kiipeilytelineet", sub: "Ikä vaihtelee mallista" },
    { to: "/iglu", label: "IGLU-palikat", sub: "6 kk +" },
  ],
  "vanhemman-opas": [
    { to: "/oppimistorni", label: "Oppimistornit", sub: "12 kk – 5 v" },
    { to: "/pikler-kolmio", label: "Pikler-kolmiot", sub: "Ikä vaihtelee mallista" },
    { to: "/kauppa", label: "Koko mallisto", sub: "0 – 6 v" },
  ],
} as const satisfies Record<CategoryId, readonly { to: string; label: string; sub: string }[]>;

/** Finnish-friendly slug for heading anchors (ä/ö → a/o, muut merkit pois). */
function slugifyFi(text: string) {
  return text
    .toLowerCase()
    .replace(/[äå]/g, "a")
    .replace(/ö/g, "o")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function BlogPost({ eyebrow, title, lead, readingMinutes, publishedISO, updatedISO, slug, heroImage, heroAlt, children }: BlogPostProps) {
  // Articles don't pass `slug` explicitly, so derive it from the URL to make
  // sure "Lue seuraavaksi" never links back to the article being read.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const currentSlug = slug ?? pathname.replace(/\/+$/, "").split("/").pop();
  const canonicalUrl = `https://montessori24.fi${pathname}`;
  const post = POSTS.find((p) => p.slug === currentSlug);
  const category = post?.category ?? "perusteet";
  const related = relatedPosts(currentSlug);
  const series = seriesPosition(currentSlug);
  const hero = heroImage ?? CATEGORY_HERO[category];
  const productPicks = CATEGORY_PRODUCTS[category];

  // Inject a contextual product callout roughly halfway through the article.
  // Splitting the children array lets us slot the promo without touching
  // per-article markup, keeping session depth up without extra editorial work.
  const kids = Children.toArray(children);
  // Some articles carry their own FAQ section in the body — skip the
  // registry-driven FAQ block for those so it never renders twice.
  const hasInlineFaq = kids.some(
    (k) =>
      typeof k === "object" && k !== null && "type" in k && k.type === "h2" &&
      typeof (k as { props?: { children?: unknown } }).props?.children === "string" &&
      ((k as { props: { children: string } }).props.children.toLowerCase().includes("kysymyks")),
  );
  const midIndex = Math.max(1, Math.floor(kids.length / 2));
  const firstHalf = kids.slice(0, midIndex);
  const secondHalf = kids.slice(midIndex);

  // Reading-progress bar — transform-driven, so no re-renders on scroll.
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? window.scrollY / max : 0;
      if (barRef.current) barRef.current.style.transform = `scaleX(${Math.min(1, Math.max(0, p))})`;
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Build the table of contents from the rendered h2 headings and give each
  // heading a stable, shareable anchor id (slugified from its text).
  const contentRef = useRef<HTMLDivElement>(null);
  const [toc, setToc] = useState<ReadonlyArray<{ id: string; text: string }>>([]);
  const [activeId, setActiveId] = useState<string>("");
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const headings = Array.from(el.querySelectorAll("h2"));
    const used = new Set<string>();
    const items = headings.map((h, i) => {
      const text = (h.textContent ?? "").trim();
      let id = slugifyFi(text) || `luku-${i + 1}`;
      while (used.has(id)) id = `${id}-${i + 1}`;
      used.add(id);
      h.id = id;
      h.style.scrollMarginTop = "88px";
      return { id, text };
    });
    setToc(items);

    // Highlight the section currently in view.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible?.target.id) setActiveId(visible.target.id);
      },
      { rootMargin: "-88px 0px -70% 0px", threshold: 0 },
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, []);


  return (
    <Layout>
      {/* Reading progress */}
      <div aria-hidden className="fixed inset-x-0 top-0 z-[60] h-[3px] bg-transparent">
        <div ref={barRef} className="h-full w-full origin-left bg-sage" style={{ transform: "scaleX(0)" }} />
      </div>

      <article className="bg-cream">
        <header className="border-b border-border">
          <div className="container-prose py-14 md:py-20">
            <nav aria-label="Murupolku" className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              <Link to="/" className="hover:text-foreground">Etusivu</Link>
              <span className="mx-2">/</span>
              <Link to="/montessori-koulu" className="hover:text-foreground">Montessori-koulu</Link>
            </nav>
            <span className="mt-6 inline-block rounded-full bg-sage/10 px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-sage">
              {eyebrow}
            </span>
            <h1 className="mt-4 max-w-3xl text-4xl text-foreground md:text-5xl text-balance">{title}</h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">{lead}</p>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" aria-hidden />
                <time dateTime={publishedISO}>{formatFi(publishedISO)}</time>
              </span>
              {updatedISO && updatedISO !== publishedISO && (
                <span>
                  Päivitetty <time dateTime={updatedISO}>{formatFi(updatedISO)}</time>
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" aria-hidden />
                {readingMinutes} min lukuaika
              </span>
            </div>

            {/* Author / editorial trust box */}
            <div className="mt-8 flex max-w-xl items-center gap-4 rounded-2xl border border-border bg-card p-4">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-sage/10 text-sage">
                <BookOpen className="h-5 w-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Montessori24 toimitus</p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                  Montessori24 kokoaa käytännöllisiä Montessori-ideoita sekä valmistajien ja luotettavien lähteiden turvallisuustietoa. Turvallisuutta koskevissa kysymyksissä noudata aina tuotteen valmistajan ohjeita ja ajantasaisia viranomaisohjeita.
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Hero image */}
        <figure className="container-prose pt-10 md:pt-12">
          <img
            src={hero}
            alt={heroAlt ?? `${title} — aihekuva`}
            width={1280}
            height={720}
            fetchPriority="high"
            className="aspect-[16/9] w-full rounded-3xl border border-border object-cover"
          />
        </figure>

        <div className="container-prose py-12 md:py-16">
          <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-[220px_minmax(0,1fr)]">
            {/* Sticky TOC — desktop */}
            {toc.length > 1 && (
              <aside className="hidden lg:block">
                <nav aria-label="Sisällysluettelo" className="sticky top-24">
                  <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">Sisällys</p>
                  <ol className="mt-4 space-y-2.5 border-l border-border">
                    {toc.map((t) => (
                      <li key={t.id}>
                        <a
                          href={`#${t.id}`}
                          aria-current={activeId === t.id ? "true" : undefined}
                          className={`-ml-px block border-l-2 pl-4 text-[13px] leading-snug transition-colors hover:border-sage hover:text-foreground ${
                            activeId === t.id ? "border-sage font-medium text-foreground" : "border-transparent text-muted-foreground"
                          }`}
                        >
                          {t.text}
                        </a>
                      </li>
                    ))}

                  </ol>
                </nav>
              </aside>
            )}

            <div className="min-w-0 max-w-2xl">
              {/* Collapsible TOC — mobile */}
              {toc.length > 1 && (
                <details className="group mb-10 rounded-2xl border border-border bg-card lg:hidden">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 text-sm font-medium text-foreground [&::-webkit-details-marker]:hidden">
                    Sisällysluettelo
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden />
                  </summary>
                  <ol className="space-y-2 px-5 pb-5">
                    {toc.map((t, i) => (
                      <li key={t.id} className="flex gap-2.5 text-sm">
                        <span aria-hidden className="tabular-nums text-muted-foreground">{i + 1}.</span>
                        <a href={`#${t.id}`} className="text-foreground underline-offset-4 hover:underline">
                          {t.text}
                        </a>
                      </li>
                    ))}
                  </ol>
                </details>
              )}

              {/* The callout must sit OUTSIDE .prose-blog: the article CSS
                  forces `a { color: var(--foreground) }`, which would override
                  the pill buttons' text-background and make them unreadable. */}
              <div ref={contentRef} className="text-foreground">
                <div className="prose-blog">{firstHalf}</div>
                <MidArticleCallout />
                <div className="prose-blog">{secondHalf}</div>
              </div>

              {/* Summary box */}
              {post?.takeaway && (
                <aside aria-label="Yhteenveto" className="mt-12 rounded-2xl border border-sage/30 bg-sage/10 p-6 md:p-7">
                  <div className="flex items-start gap-4">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-sage/15 text-sage">
                      <Lightbulb className="h-5 w-5" aria-hidden />
                    </span>
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-sage">Yhteenveto</p>
                      <p className="mt-2 text-[15px] leading-relaxed text-foreground">{post.takeaway}</p>
                    </div>
                  </div>
                </aside>
              )}

              {/* FAQ — accordion + FAQPage JSON-LD */}
              {post?.faqs && post.faqs.length > 0 && !hasInlineFaq && (
                <section aria-label="Usein kysytyt kysymykset" className="mt-12">
                  <h2 className="font-display text-2xl text-foreground md:text-3xl text-balance">Usein kysytyt kysymykset</h2>
                  <div className="mt-5 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
                    {post.faqs.map((f) => (
                      <details key={f.q} className="group">
                        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 text-[15px] font-medium text-foreground transition-colors hover:bg-sand/40 [&::-webkit-details-marker]:hidden">
                          {f.q}
                          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden />
                        </summary>
                        <p className="px-5 pb-5 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                      </details>
                    ))}
                  </div>
                  <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                      __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        mainEntity: post.faqs.map((f) => ({
                          "@type": "Question",
                          name: f.q,
                          acceptedAnswer: { "@type": "Answer", text: f.a },
                        })),
                      }),
                    }}
                  />
                </section>
              )}

              {/* Sources */}
              {post?.sources && post.sources.length > 0 && (
                <section aria-label="Lähteet" className="mt-10 border-t border-border pt-8">
                  <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">Lähteet</p>
                  <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
                    {post.sources.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ol>
                </section>
              )}

              {/* Share bar */}
              <ShareBar url={canonicalUrl} title={title} description={post?.excerpt} />

              {/* Learning-path navigation */}
              {series && (
                <nav aria-label="Artikkelisarja" className="mt-12 rounded-2xl border border-border bg-card p-6">
                  <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">
                    {series.label} · Osa {series.part}/{series.total}
                  </p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {series.previous ? (
                      <Link
                        to={series.previous.to}
                        className="group rounded-xl border border-border bg-background p-4 transition-colors hover:border-foreground"
                      >
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">← Edellinen artikkeli</span>
                        <span className="mt-1.5 block text-sm font-medium leading-snug text-foreground group-hover:underline">
                          {series.previous.shortTitle}
                        </span>
                      </Link>
                    ) : (
                      <span aria-hidden />
                    )}
                    {series.next && (
                      <Link
                        to={series.next.to}
                        className="group rounded-xl border border-border bg-background p-4 text-right transition-colors hover:border-foreground sm:col-start-2"
                      >
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Seuraava artikkeli →</span>
                        <span className="mt-1.5 block text-sm font-medium leading-snug text-foreground group-hover:underline">
                          {series.next.shortTitle}
                        </span>
                      </Link>
                    )}
                  </div>
                </nav>
              )}

              {/* Related reading — internal linking */}
              <aside aria-label="Lue seuraavaksi" className="mt-14 border-t border-border pt-10">
                <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">Jatka lukemista</p>
                <ul className="mt-5 grid gap-4 sm:grid-cols-3">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={r.to}
                        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-0.5 hover:border-foreground"
                      >
                        <div className="aspect-[16/9] overflow-hidden bg-sand">
                          <img
                            src={CATEGORY_HERO[r.category]}
                            alt=""
                            width={1280}
                            height={720}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                          />
                        </div>
                        <div className="flex flex-1 flex-col gap-2 p-4">
                          <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">{r.eyebrow}</span>
                          <span className="flex-1 text-sm font-medium leading-snug text-foreground group-hover:underline">{r.shortTitle}</span>
                          <span aria-hidden className="text-xs text-clay">Lue →</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/montessori-koulu"
                  className="mt-6 inline-flex text-sm font-medium text-foreground underline underline-offset-4 hover:text-clay"
                >
                  ← Kaikki Montessori-koulun oppaat
                </Link>
              </aside>

              {/* Age-matched product picks — conversions through education */}
              <aside aria-label="Aiheeseen sopivat tuotteet" className="mt-10">
                <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">Suosittelemme aiheeseen</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {productPicks.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      className="group flex flex-col gap-1 rounded-2xl border border-border bg-sand/40 p-4 transition-colors hover:border-foreground"
                    >
                      <span className="text-sm font-medium text-foreground">{p.label} →</span>
                      <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground group-hover:text-foreground">{p.sub}</span>
                    </Link>
                  ))}
                </div>
              </aside>

              <div className="mt-12 flex flex-col items-start gap-4 border-t border-border pt-8 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-muted-foreground">Valmis tuomaan opit kotiin?</p>
                <Link
                  to="/kauppa"
                  className="inline-flex items-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Selaa Montessori-kalusteita →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}

/* ---------- Editorial building blocks for article bodies ---------- */

/** Highlighted aside box for key ideas or warnings inside an article. */
export function Callout({ title, tone = "sage", children }: { title: string; tone?: "sage" | "clay"; children: React.ReactNode }) {
  const styles =
    tone === "clay"
      ? "border-clay/30 bg-clay/10 text-clay"
      : "border-sage/30 bg-sage/10 text-sage";
  return (
    <aside className={`not-prose my-8 rounded-2xl border p-5 md:p-6 ${styles}`}>
      <p className="text-[11px] font-medium uppercase tracking-[0.22em]">{title}</p>
      <div className="mt-2 text-[15px] leading-relaxed text-foreground">{children}</div>
    </aside>
  );
}

/** Checklist with sage check icons — great for "muista nämä" sections. */
export function Checklist({ items }: { items: readonly string[] }) {
  return (
    <ul className="not-prose my-8 space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-[15px] leading-relaxed text-foreground">
          <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-sage/15 text-sage">
            <Check className="h-3 w-3" aria-hidden />
          </span>
          {item}
        </li>
      ))}
    </ul>
  );
}

/** Large editorial pull quote. */
export function PullQuote({ children, by }: { children: React.ReactNode; by?: string }) {
  return (
    <figure className="not-prose my-10 border-l-2 border-clay pl-6">
      <blockquote className="font-display text-2xl leading-snug text-foreground md:text-[26px] text-balance">{children}</blockquote>
      {by && <figcaption className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">{by}</figcaption>}
    </figure>
  );
}

function formatFi(iso: string) {
  const [y, m, d] = iso.split("-");
  const months = ["tammikuuta","helmikuuta","maaliskuuta","huhtikuuta","toukokuuta","kesäkuuta","heinäkuuta","elokuuta","syyskuuta","lokakuuta","marraskuuta","joulukuuta"];
  return `${parseInt(d, 10)}. ${months[parseInt(m, 10) - 1]} ${y}`;
}

export function articleJsonLd(args: {
  title: string;
  description: string;
  url: string;
  image?: string;
  publishedISO: string;
  updatedISO?: string;
  /** Aihealue, esim. "Turvallisuus". Oletuksena artikkelin kategoria. */
  section?: string;
  keywords?: readonly string[];
  readingMinutes?: number;
}) {
  const image = args.image ?? "https://montessori24.fi/og-image.jpg";
  const slug = args.url.replace(/\/+$/, "").split("/").pop() ?? "";
  const post = POSTS.find((p) => p.slug === slug);
  // Google näyttää enintään ~110 merkkiä otsikosta.
  const headline = args.title.length > 110 ? `${args.title.slice(0, 107).trimEnd()}…` : args.title;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${args.url}#article`,
    headline,
    name: args.title,
    description: args.description,
    inLanguage: "fi-FI",
    url: args.url,
    datePublished: args.publishedISO,
    dateModified: args.updatedISO ?? args.publishedISO,
    mainEntityOfPage: { "@type": "WebPage", "@id": args.url },
    image: [
      {
        "@type": "ImageObject",
        url: image,
        width: 1280,
        height: 720,
      },
    ],
    articleSection:
      args.section ??
      CATEGORIES.find((c) => c.id === post?.category)?.label ??
      "Montessori-koulu",
    ...(args.keywords?.length ? { keywords: args.keywords.join(", ") } : {}),
    ...(args.readingMinutes
      ? { timeRequired: `PT${Math.round(args.readingMinutes)}M` }
      : {}),
    isAccessibleForFree: true,
    author: {
      "@type": "Organization",
      name: "Montessori24 toimitus",
      url: "https://montessori24.fi/montessori-koulu",
    },
    publisher: {
      "@type": "Organization",
      name: "Montessori24",
      url: "https://montessori24.fi",
      logo: { "@type": "ImageObject", url: "https://montessori24.fi/og-image.jpg" },
    },
  };
}


export function breadcrumbJsonLd(slug: string, title: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://montessori24.fi/" },
      { "@type": "ListItem", position: 2, name: "Montessori-koulu", item: "https://montessori24.fi/montessori-koulu" },
      { "@type": "ListItem", position: 3, name: title, item: `https://montessori24.fi/montessori-koulu/${slug}` },
    ],
  };
}

/**
 * Inline promo shown mid-article. Kept intentionally understated so it
 * reads as a helpful aside rather than an ad break.
 */
function MidArticleCallout() {
  return (
    <aside
      aria-label="Aiheeseen liittyvä tuote"
      className="not-prose my-10 rounded-2xl border border-border bg-sand/40 p-5 md:p-6"
    >
      <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">
        Aiheeseen liittyvä
      </p>
      <div className="mt-2 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-lg font-medium text-foreground">
            Pikler-kolmio &amp; oppimistorni
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Suomalaisten perheiden suosikit — ilmainen toimitus yli 79 €.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/pikler-kolmio"
            className="inline-flex items-center rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background transition-transform hover:-translate-y-0.5"
          >
            Pikler-kolmiot →
          </Link>
          <Link
            to="/oppimistorni"
            className="inline-flex items-center rounded-full border border-foreground px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-foreground hover:text-background"
          >
            Oppimistornit →
          </Link>
        </div>
      </div>
    </aside>
  );
}